import { Controller } from "@hotwired/stimulus"
import { loadJs } from '../models/external_script_loader';

let stripe;
const STRIPE_FIELDS = {
  cardNumber: {
    key: 'number',
    selector: '#error-number',
    required: 'Please enter card number',
    label: 'Card Number'
  },
  cardExpiry: {
    key: 'expiry',
    selector: '#error-expiry',
    required: 'Please enter expiration date',
    label: 'Expiration Date'
  },
  cardCvc: {
    key: 'cvc',
    selector: '#error-cvc',
    required: 'Please enter CVV',
    label: 'CVV'
  }
}
// Connects to data-controller="stripe-setup-intent"
export default class StripeSetupIntentController extends Controller {
  static values = { clientSecret: String
  }

  async connect() {
    await loadJs('https://js.stripe.com/v3/');
    this.mode = this.element.getAttribute("data-mode") || "setup"

    this.stripe = Stripe(this.element.getAttribute("data-key"))
    stripe = this.stripe

    if (this.mode === "confirm") {
      if (this.element.dataset.confirming === "1") return
      this.element.dataset.confirming = "1"

      this.setState('loading')
      this.confirmPayment()
      return
    }

    const elements = this.stripe.elements()

    const elementStyles = {
      base: {
        color: 'var(--white)',
        fontWeight: 400,
        fontFamily: 'var(--font-primary)',
        fontSize: '14px',
        ':focus': { color: 'var(--white)' },
        '::placeholder': { color: 'var(--gray)' },
        ':-webkit-autofill': { color: 'var(--white)' },
        ':focus::placeholder': { color: 'var(--gray)' }
      },
      invalid: {
        color: 'var(--white)',
        ':focus': { color: 'var(--danger)' },
        '::placeholder': { color: 'var(--gray)' }
      }
    };

    this.cardComplete = {
      number: false,
      expiry: false,
      cvc: false
    }

    const elementClasses = { focus: 'focus', empty: 'empty', invalid: 'invalid' };

    const cardNumber = elements.create('cardNumber', { style: elementStyles, classes: elementClasses });
    cardNumber.mount('#stripe-card-number');

    const cardExpiry = elements.create('cardExpiry', { style: elementStyles, classes: elementClasses });
    cardExpiry.mount('#stripe-card-expiry');

    const cardCvc = elements.create('cardCvc', { style: elementStyles, classes: elementClasses, placeholder: 'CVV' });
    cardCvc.mount('#stripe-card-cvc');

    this.registerElements([cardNumber, cardExpiry, cardCvc], 'stripe_form');
  }

  handleStripeError(error) {
    const msg = (error?.message || "").toLowerCase()

    if (msg.includes("canceled") || msg.includes("cancelled")) {
      this.setState("cancel")
      return
    }

    this.setState("error", error.message || "Payment failed. Please try again.")
  }

  retryConfirm(e) {
    e?.preventDefault?.()

    if (this.element.dataset.confirming === "1") return
    this.element.dataset.confirming = "1"

    this.setState("loading")
    this.confirmPayment()
  }

  setState(state, message = null) {
    // hide all optional parts
    this.hide("#stripe-error")
    this.hide("#stripe-actions")
    this.hide("#stripe-loader")

    if (state === "loading") {
      this.setText("#stripe-title", "Confirming your payment")
      this.setText("#stripe-status", "Please wait… don’t close this window.")
      this.show("#stripe-loader")
      return
    }

    if (state === "cancel") {
      this.setText("#stripe-title", "Payment confirmation cancelled")
      this.setText("#stripe-status", "You cancelled the verification. You can try again anytime.")
      this.show("#stripe-actions")
      return
    }

    // error
    this.setText("#stripe-title", "Payment could not be completed")
    this.setText("#stripe-status", "Please review the error and try again.")
    this.setText("#stripe-error", message || "Something went wrong.")
    this.show("#stripe-error")
    this.show("#stripe-actions")
  }

  el(selector) {
    return this.element.querySelector(selector)
  }

  setText(selector, text) {
    const node = this.el(selector)
    if (node) node.textContent = text
  }

  hide(selector) {
    const node = this.el(selector)
    if (node) node.classList.add("d-none")
  }

  show(selector) {
    const node = this.el(selector)
    if (node) node.classList.remove("d-none")
  }

  async confirmPayment(e) {
    e?.preventDefault?.()

    const {clientSecret, pm, reload, finalizePayload, sentNotification} = this.element.dataset
    if (!clientSecret || !pm) {
      this.element.dataset.confirming = "0"
      this.setState("error", "Missing payment data. Please refresh and try again.")
      return
    }

    const result = await this.stripe.confirmCardPayment(clientSecret, 
      { payment_method: pm },
      { handleActions: true }
    )

    if (result.error) {
      this.handleStripeError(result.error)
      this.element.dataset.confirming = "0"
      return
    }

    const paymentIntentId = result.paymentIntent?.id
    if (!paymentIntentId) {
      this.setState?.("error", "Payment succeeded but payment intent missing.")
      this.hide("#stripe-actions")
      return
    }

    let extraPayload = {}
    try {
      extraPayload = finalizePayload ? JSON.parse(finalizePayload) : {}
    } catch (e) {
      extraPayload = {}
    }

    const payload = {
      payment_intent_id: paymentIntentId,
      sent_notification: sentNotification,
      ...extraPayload
    }

    fetch(`/stripe/finalize`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify(payload)
    }).then(async (res) => {
      if (res.redirected) {
        globalThis.location.href = res.url
        return '';
      }
      if (res.ok) {
        if (reload === 'true') {
          globalThis.location.reload()
        } else {
          const html = await res.text()
          Turbo.renderStreamMessage(html)

          const modalIds = ["#smallModal", ...JSON.parse(this.element.dataset.modalIds || "[]")];

          for (const id of modalIds){
            const el = document.querySelector(id)
            if (!el) return

            const modal = bootstrap.Modal.getOrCreateInstance(el)
            modal.hide()
          }
        }
      } else {
        const data = await res.json().catch(() => ({}))
        this.setState?.("error", data.error || "Could not finalize payment. Please contact support.")
        this.hide("#stripe-actions")
        return
      }
    }).catch(() => {
      this.setState?.('error', 'Could not finalize payment. Please contact support.')
      this.hide('#stripe-actions')
    })
  }

  registerElements(elements, formClassName) {
    const formContainer = document.querySelector(`.${formClassName}`);
    const form = formContainer.querySelector('form');
    let savedErrors = {};

    for (const [idx, element] of elements.entries()) {
      element.on('change', (event) => {
        const field = STRIPE_FIELDS[event.elementType]
        this.cardComplete[field.key] = !event.empty

        if (event.error) {
          const message = event.empty ? field.required : event.error.message.replace('security code', 'CVV')
          this.showFieldError(event.elementType, message)
          savedErrors[idx] = message
        } else {
          this.clearFieldError(event.elementType)
          savedErrors[idx] = null
        }
      })
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const stripeError = this.validateStripeFields()
      if (stripeError) return

      $('#new_user_card').find('.add-card').prop('disabled', true).html(`
        <span class='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
        <span>Loading...</span>
      `);

      const name_val = $('#stripe-first-name').val();
      const address1_val = $('#stripe-card-address1').val();
      const address2_val = $('#stripe-card-address2').val();
      const city_val = $('#stripe-card-city').val();
      const state_val = $('#stripe-card-state').val();
      const country_val = $('#stripe-country').val();
      const zip_val = $('#stripe-card-zip').val();

      const billingDetails = {
        name: name_val,
        address: {
          line1: address1_val,
          line2: address2_val,
          city: city_val,
          state: state_val,
          country: country_val,
          postal_code: zip_val
        }
      };

      try {
        const clientSecret = this.element.getAttribute('data-client-secret');

        console.log('clientSecret', clientSecret)
        const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: elements[0],
            billing_details: billingDetails
          }
        });
        console.log('billingDetails', billingDetails)

        if (error) {
          this.showError(error.type === 'validation_error' ? '' : error.message);
          console.error(error.message);
          $('#new_user_card').find('.add-card').prop('disabled', false).html('ADD NEW CARD');
        } else if (setupIntent && setupIntent.status === 'succeeded') {
          stripeTokenHandler(setupIntent.payment_method);
        }
      } catch (err) {
        console.error(err);
        this.showError('An unexpected error occurred.');
        $('#new_user_card').find('.add-card').prop('disabled', false).html('ADD NEW CARD');
      }
    });

    function stripeTokenHandler(paymentMethodId) {
      const form = document.getElementById('new_user_card');
      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = 'stripePaymentMethod';
      hiddenInput.value = paymentMethodId;
      form.appendChild(hiddenInput);

      const redirect_url = form.dataset.redirectUrl;
      const url = form.getAttribute('action');
      const method = form.getAttribute("data-turbo-method");

      fetch(url, {
        method: method,
        headers: {
          Accept: redirect_url == null ? "text/vnd.turbo-stream.html" : "text/html",
          'Content-Type': 'application/json',
          "X-CSRF-Token": document.getElementsByName("csrf-token")[0].content,
        },
        body: JSON.stringify({
          stripePaymentMethod: paymentMethodId,
          is_wallet: document.getElementById("is_wallet").value,
          is_subscribe: document.getElementById("is_subscribe").value,
          is_order: document.getElementById("is_order").value,
          new_card:
            document.getElementById("new_card") == null
              ? false
              : document.getElementById("new_card").value,
          user_id: $("#user_id").val(),
          address_1: $(".address1").val(),
          address_2: $(".address2").val(),
          city: $(".city").val(),
          state: $(".state").val(),
          postal_code: $(".zip").val(),
          name: $(".name").val(),
          country: $(".country").val()
        })
      }).then((r) => {
        if (r.ok && !r.redirected) {
          return r.text();
        } else {
          if (r.redirected) {
            globalThis.location.href = r.url;
          }
          return r.text();
        }
      })
      .then((response) => {
        $("#new_user_card")
          .find(".add-card")
          .prop("disabled", false)
          .html(
            '<span class="text-black-border bg-text-white d-block transparent-bg-text fs-22 p-font-bold fst-italic">' +
            ($("#new_card")?.val() === "true" ? "Add Card" : "Pay") +
            "</span>"
          );
        if (redirect_url === null || redirect_url === undefined) {
          let card_modal = document.getElementById("new-card-form");
          const modal = bootstrap.Modal.getInstance(card_modal);
          const errorRegex = /target="error"/;
          if (!errorRegex.exec(response)) {
            if (modal != null && !['/showcases', '/shopping_bags/shopping_cart'].includes(globalThis.location.pathname)) {
              modal.hide();
            }
          }
          if (errorRegex.exec(response)) {
            $("#new_user_card")
              .find(".add-card")
              .prop("disabled", false)
              .html(
                '<span class="text-black-border bg-text-white d-block transparent-bg-text fs-22 p-font-bold fst-italic">Pay</span>'
              );
          }
          Turbo.renderStreamMessage(response);
        } else {
          globalThis.location.href = redirect_url;
        }
      });
    }
  }

  showError(msg) {
    document.querySelector(`#error`).innerText = msg
  }

  validateStripeFields() {
    let hasError = false

    for (const [type, field] of Object.entries(STRIPE_FIELDS)) {
      if (!this.cardComplete[field.key]) {
        this.showFieldError(type, field.required)
        hasError = true
      }
    }

    return hasError
  }

  showFieldError(type, message) {
    const field = STRIPE_FIELDS[type]
    if (!field) return

    const error = document.querySelector(field.selector)
    if (!error) return

    error.textContent = message
    error.classList.remove("d-none")
  }

  clearFieldError(type) {
    const field = STRIPE_FIELDS[type]
    if (!field) return

    const error = document.querySelector(field.selector)
    if (!error) return

    error.textContent = ""
    error.classList.add("d-none")
  }
}
